import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation, useQuery } from '@apollo/client/react'
// graphql
import { OBJECT_ENTITIES_QUERY } from 'graphql/objects/queries'
import { PLAN_TYPES_DETAILS_QUERY } from 'graphql/planType/queries'
import {
  FIELD_CREATE_MUTATION,
  FIELD_DELETE_MUTATION,
  FIELD_UPDATE_MUTATION,
  FIELD_UPDATE_POSITIONS_MUTATION
} from 'graphql/objects/mutations'
// constants
import { ENTITIES } from 'constants/entities'
// types
import { IOption } from 'components/FormElements/Select'
import { TRefetch } from 'types/refetch'
import { NetworkStatus } from '@apollo/client'
import { FieldType, IField, IFieldResponse, IObjectEntity } from 'types/graphql'
// utils
import { get, omit } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { filter, pipe, sortBy } from 'lodash/fp'
import { normalizeFieldProperty } from 'utils/fields'
import { getValueFromOption, safeGetOr } from 'utils/index'

type FieldUpdateResponse = { fieldUpdate: IFieldResponse }

interface IContext {
  object: IObjectEntity
  fields: IField[]
  loading: boolean
  refetch: TRefetch<any, any>
  createField: (field: IField, position: string, id: string, isPlanType: boolean, shouldAddParentField: boolean) => void
  updateField: (data: any, field: IField) => void
  deleteField: (id: string, isPlanType?: boolean) => void
  updatePositions: (fields: any[], isPlanType: boolean) => void
}

export const ObjectsAPIContext = React.createContext<IContext>({} as any)

const ObjectsAPI = ({ children }) => {
  const router = useRouter()

  const { data, refetch, networkStatus } = useQuery(OBJECT_ENTITIES_QUERY, {
    nextFetchPolicy: 'network-only',
    variables: {
      filter: {
        id: router.query.id
      }
    }
  })

  const [fieldCreateMutation] = useMutation(FIELD_CREATE_MUTATION)
  const [fieldDeleteMutation] = useMutation(FIELD_DELETE_MUTATION)
  const [fieldUpdateMutation] = useMutation<FieldUpdateResponse>(FIELD_UPDATE_MUTATION)
  const [fieldUpdatePositionsMutation] = useMutation(FIELD_UPDATE_POSITIONS_MUTATION)

  const object: IObjectEntity = safeGetOr<any, any, any>(false, 'objectEntities.data[0]')(data)

  const fields = pipe(
    safeGetOr<any, any, any>([], 'objectEntities.data[0].fields'),
    sortBy((field: IField) => field.position),
    filter((field: IField) => field.name !== 'completionPercentage')
  )(data)

  const createField = async (
    field: IField,
    position: string,
    id: string,
    isPlanType: boolean,
    shouldAddParentField: boolean
  ) => {
    try {
      await fieldCreateMutation({
        variables: {
          input: {
            active: true,
            required: !!field.required,
            restricted: !!field.restricted,
            multiline: !!field.multiline,
            wysiwyg: !!field.wysiwyg,
            label: field.label,
            format: normalizeFieldProperty(field.format),
            multiple: !!field.multiple,
            source: field.source || [],
            searchTemplateIds: field.searchTemplateIds,
            options: normalizeFieldProperty(field.options),
            standard: false,
            position: position,
            entityId: isPlanType ? id : null,
            fieldType: field.fieldType,
            parentFieldId: shouldAddParentField ? field.id : null,
            objectEntityId: isPlanType ? null : id
          }
        },
        refetchQueries: isPlanType
          ? [
              {
                query: PLAN_TYPES_DETAILS_QUERY,
                variables: {
                  filter: {
                    ids: [router.query.planType]
                  }
                }
              }
            ]
          : []
      })

      await refetch()
    } catch (errors) {
      handleError(errors, 'ObjectsAPI.createField')
    }
  }

  const updateField = async (data: Record<string, any>, field: IField) => {
    try {
      const source = data.source?.filter((item: any) => item.entity !== ENTITIES.RISK).map(getValueFromOption)
      const searchTemplateIds = data.source
        ?.filter((item: any) => item.entity === ENTITIES.RISK)
        .map(getValueFromOption)

      const {
        data: {
          fieldUpdate: {
            data: { options }
          }
        }
      } = await fieldUpdateMutation({
        variables: {
          id: field.id,
          input: {
            active: data.active,
            required: data.required,
            restricted: data.restricted,
            multiline: !!data.multiline,
            wysiwyg: data.wysiwyg,
            label: data.name,
            format: normalizeFieldProperty([data.format]),
            multiple: !!data.multiple,
            readonly: data.readonly,
            source,
            searchTemplateIds,
            options: get(data, 'options', []).map((option: IOption[]) => omit(option, '__typename')),
            ...(field.fieldType === FieldType.Date && {
              settings: {
                format: getValueFromOption(data.settings.format),
                dateFormat: data.settings.dateFormat?.trim()
              }
            })
          }
        },
        refetchQueries: router.query.planType
          ? [
              {
                query: PLAN_TYPES_DETAILS_QUERY,
                variables: {
                  filter: {
                    ids: [router.query.planType]
                  }
                }
              }
            ]
          : []
      })

      refetch()

      return options
    } catch (errors) {
      handleError(errors, 'ObjectsAPI.updateField')
    }
  }

  const deleteField = async (id: string, isPlanType: boolean) => {
    try {
      await fieldDeleteMutation({
        variables: {
          id: id
        },
        refetchQueries: isPlanType
          ? [
              {
                query: PLAN_TYPES_DETAILS_QUERY,
                variables: {
                  filter: {
                    ids: [router.query.planType]
                  }
                }
              }
            ]
          : []
      })

      await refetch()
    } catch (errors) {
      handleError(errors, 'ObjectsAPI.deleteField')
    }
  }

  const updatePositions = async (fields: IField[], isPlanType: boolean) => {
    try {
      await fieldUpdatePositionsMutation({
        variables: {
          input: fields.map((item, i) => ({ id: item.id, position: i }))
        },
        refetchQueries: isPlanType
          ? [
              {
                query: PLAN_TYPES_DETAILS_QUERY,
                variables: {
                  filter: {
                    ids: [router.query.planType]
                  }
                }
              }
            ]
          : []
      })

      await refetch()
    } catch (errors) {
      handleError(errors, 'ObjectsAPI.updatePositions')
    }
  }

  const value = {
    object,
    fields,
    refetch,
    loading: networkStatus === NetworkStatus.loading,
    createField,
    updateField,
    deleteField,
    updatePositions
  } as any

  return <ObjectsAPIContext.Provider value={value}>{children}</ObjectsAPIContext.Provider>
}

export default ObjectsAPI
